// this function will create a new column config if the table is meant to expand
import React from 'react';
import { Column, Row, TableState } from 'react-table';
import _ from 'lodash';
import { Icon } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { ColumnGeneratorConfig } from '../types';
import styles from './index.css';

// and if the table is meant to expand all the rows, just one of the rows, or some permeutation of this
const createColumns = <D extends object = {}>({ icon = {}, columns, dataTestId }: ColumnGeneratorConfig<D>) => {
    const { showIcon, iconType } = icon;
    if (!showIcon) return columns;
    const newColumns = _.clone(columns);

    const collapseIcon = iconType === 'vertical' ? 'arrow-up-1' : 'arrow-left-1';
    const expandIcon = iconType === 'vertical' ? 'arrow-down-1' : 'arrow-right-1';

    newColumns.unshift({
        id: `${dataTestId}-expander`,
        style: {
            maxWidth: 40
        },
        disableSortBy: true,
        renderType: 'overrideCell',
        Header: () => null,
        Cell: ({ row }: { row: Row<D> }) => {
            if (row.cells.some((cell) => cell.isGrouped)) return null;
            const isOpen = row?.isExpanded || row?.isSplit;
            return (
                <Icon
                    className={styles.expandContainer}
                    name={isOpen ? collapseIcon : expandIcon}
                    aria-label={isOpen ? 'collpase row' : 'expand row'}
                />
            );
        }
    });

    return newColumns;
};

const createInitialState = <D extends object>({
    initialState: iState,
    data,
    columns,
    reduxInitialState
}: {
    initialState: Partial<TableState<D>>;
    data: D[];
    columns: Column<D>[];
    reduxInitialState: Partial<TableState<D>>;
}) => {
    // split view is controlled so can't really use it
    const initialState = _.omit(_.merge({}, iState, reduxInitialState), 'splitViewRowId');

    if (!initialState?.groupBy || initialState.groupBy?.length === 0) return { ...initialState };
    const groupByColumns = columns.filter((aCol) => initialState?.groupBy?.includes(aCol.id as string));
    //  no need to run if we don't have groupBy columns
    if (groupByColumns.length === 0) return { ...initialState };

    /**  
     react-table expanded object expects us to provide
     the row.id as the key and a boolean value for the value
     However, and this is an oversight maybe on Tanner's part
     but we can't override the row.ids reliabley for grouped rows.
     
     the only way to "initially expand" all the groups is to loop over the data
     and do the same construction react-table does for the ids here
     */
    // eslint-disable-next-line max-len
    //  https://github.com/tannerlinsley/react-table/blob/8ec25cabccf915991ec00cf67bfb26a83aa0c0b2/src/plugin-hooks/useGroupBy.js#L311-L312
    if (!initialState.expanded) {
        const expanded = data?.reduce((acc, curr) => {
            const newAcc = { ...acc };
            groupByColumns?.forEach((aCol) => {
                // if the user is providing us their own static group by order
                // we can assume that they want them expanded as well
                _.forEach(aCol?.groupByOrder, (_val, key) => {
                    const groupByOrderKey = `${aCol?.id}:${key}`;
                    // eslint-disable-next-line no-prototype-builtins
                    if (!newAcc?.hasOwnProperty(groupByOrderKey)) {
                        newAcc[groupByOrderKey] = true;
                    }
                });

                let groupValue = null;
                if (_.isString(aCol?.accessor)) groupValue = _.get(curr, aCol?.accessor || '');
                if (_.isFunction(aCol?.accessor)) groupValue = (aCol?.accessor as Function)?.(curr);
                // return null will "continue the loop"
                if (groupValue === null) return null;

                const id = `${aCol.id}:${groupValue}`;
                // if we already encountered this, exit out
                // eslint-disable-next-line no-prototype-builtins
                if (newAcc?.hasOwnProperty(id)) return null;
                newAcc[id] = true;
                return null;
            });
            return newAcc;
        }, initialState.expanded ?? ({} as Record<string, boolean>));

        return { ...initialState, expanded };
    }

    return { ...initialState };
};

export const useGetColumnConfig = <D extends object = {}>() => {
    const { t } = useTranslation();

    const getColConfig = React.useCallback(
        ({ Header, accessor, ...rest }: Column<D>): Column<D> => {
            const header = _.isString(Header) && Header !== '' ? t(Header) : Header;

            switch (rest?.dataType) {
                case 'datetime': {
                    return {
                        Header: header,
                        accessor,
                        preformattedValue: (d) => _.get(d, String(accessor)),
                        renderType: 'datetime',
                        sortType: 'datetime',
                        ...rest
                    } as Column<D>;
                }
                default: {
                    return {
                        Header: header,
                        accessor,
                        ...rest
                    } as Column<D>;
                }
            }
        },
        [t]
    );

    return {
        getColConfig
    };
};

export { createColumns, createInitialState };
